import React, { useEffect, useRef, useState } from 'react';
import { AlertCircleIcon, Loader2Icon } from 'lucide-react';
import * as pdfjs from 'pdfjs-dist';

/*
 * PDF'en tegnes selv side for side på <canvas> med pdf.js i stedet for at
 * blive vist i en <iframe>. Browserens indbyggede PDF-visning er blokeret i
 * indlejrede/sandboxede vinduer og findes slet ikke på de fleste mobiler —
 * derfor kom forhåndsvisningen ikke frem.
 */
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;
interface Props {
  /** Den færdige PDF som bytes — præcis samme fil som download. */
  data: ArrayBuffer;
  title: string;
}
export function PdfCanvasViewer({
  data,
  title
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let cancelled = false;
    container.innerHTML = '';
    setStatus('loading');

    // pdf.js overtager bufferen, så der sendes en kopi.
    const task = pdfjs.getDocument({
      data: new Uint8Array(data.slice(0))
    });
    task.promise.then(async pdf => {
      const ratio = window.devicePixelRatio || 1;
      const available = Math.max(container.clientWidth - 32, 280);
      for (let number = 1; number <= pdf.numPages; number += 1) {
        if (cancelled) return;
        const page = await pdf.getPage(number);
        const base = page.getViewport({
          scale: 1
        });
        const cssWidth = Math.min(available, 820);
        const scale = cssWidth / base.width * ratio;
        const viewport = page.getViewport({
          scale
        });
        const canvas = document.createElement('canvas');
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        canvas.style.width = `${cssWidth}px`;
        canvas.style.height = `${(viewport.height / ratio).toFixed(0)}px`;
        canvas.className = 'mx-auto block bg-white shadow-sm ring-1 ring-black/5';
        canvas.setAttribute('role', 'img');
        canvas.setAttribute('aria-label', `${title} – side ${number} af ${pdf.numPages}`);
        const context = canvas.getContext('2d');
        if (!context) throw new Error('canvas-unavailable');
        await page.render({
          canvasContext: context,
          viewport
        }).promise;
        if (cancelled) return;
        container.appendChild(canvas);
        if (number === 1) setStatus('ready');
      }
    }).catch((error: unknown) => {
      if (cancelled) return;
      console.error('[PDF] forhåndsvisning fejlede', error);
      setStatus('error');
    });
    return () => {
      cancelled = true;
      void task.destroy();
    };
  }, [data, title]);
  return <div className="h-full overflow-y-auto bg-canvas px-4 py-6" data-id="element-389" data-mp="25p3dqjujnj8k">
      {status === 'loading' && <p className="flex items-center justify-center gap-2 p-10 text-sm text-ink-muted" data-id="element-390" data-mp="20pqoxtk4423c">
          <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-391" data-mp="28a2n9p6drt5o" />
          Viser PDF …
        </p>}
      {status === 'error' && <p role="alert" className="flex items-center justify-center gap-2 p-10 text-sm text-danger" data-id="element-392" data-mp="2d7ba432h4ld9">
          <AlertCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-393" data-mp="2i04cwledtjmp" />
          Forhåndsvisningen kunne ikke vises. Brug “Download PDF” i stedet.
        </p>}
      <div ref={containerRef} className="flex flex-col gap-5" data-id="element-394" data-mp="1pbqe43fgyja3" />
    </div>;
}